/**
 * `/app/account/matching-preferences` — every matching input, on one screen (`MEM-009`).
 *
 * ## What this closes
 *
 * Before it, **interests and the current goal were collected once at registration and could never
 * be changed.** They are the two strongest matching signals, and
 * `docs/design/member-app-parity.md` §11 named the consequence: a beta member whose interests are
 * wrong on 23 September has wrong matches for every event after it. The report's **Q5** asks the
 * client whether this screen was in scope before the beta; it is built, so the question is
 * answered by having it rather than by ruling on it.
 *
 * ## Two three-option controls, not the prototype's one
 *
 * The prototype draws a single **Social comfort level** group whose options are One-on-one / Small
 * groups / Work the room — which are *formats*, not comfort. `MEM-009` separates them: AC-5 is
 * social comfort (Natural connector · Prefer small groups · Appreciate a warm intro) and AC-6 is
 * preferred connection format (1:1 · Small group · Open mingling), and the platform taxonomy has
 * both vocabularies seeded, single-select for a member, since `ADM-001`. Building the story's two
 * is right; the prototype conflated them. Recorded on the PR.
 *
 * **The option labels come from the taxonomy, not from the AC.** An administrator authors and
 * renames these terms (`ADM-002`), so rendering the story's strings would fork an editable list —
 * the seed says "May appreciate a warm intro" and "1:1" where AC-5 and AC-6 say "Appreciate a warm
 * intro" and "1:1 introductions". The wording difference is the taxonomy's to settle.
 *
 * ## Deliberately not here: Matches per event
 *
 * The prototype's Up to 3 / Up to 5 / Up to 8 segmented control. `MEM-009` lists the cap under Out
 * of scope and its open question explains why it cannot be a profile control at all: the member's
 * preference may only *lower* the event's number, so the control needs an event and the effective
 * cap is resolved at generation time. Three fixed choices contradict that.
 *
 * ## The 10 September prototype's arrangement, minus its contradictions
 *
 * `Web App.dc.html`'s `showPrefs` (lines 830–894) is the layout: back link, 4xl title over a 700px
 * lede, five 24px section cards each headed at `md`/700 over a `sm` hint, then Save, then a rule
 * and the optional block under "Improve your matches".
 *
 * What is **not** taken, all of it entry 130 of `handoff-corrections.md`: the goal does not default
 * to "Big Goal!"; the second Save is a real control with a real enabled state rather than the
 * prototype's purple-300 button that reads as disabled and does nothing; the counters and limits
 * the prototype states and does not enforce are enforced here (`matchingPreferencesAreSavable`);
 * and the option labels stay the taxonomy's, which is the vocabulary the entry asks for.
 */
import {
  Button,
  Card,
  CardContent,
  Field,
  SelectableCard,
  Skeleton,
  Textarea,
  ToggleChip,
  useErrorToast,
} from '@h2hspark/ui';
import * as React from 'react';

import { PageBackLink } from '@/member/page-back-link';

import {
  useRegistrationVocabularies,
  useFullMatchingPreferences,
  useSaveFullMatchingPreferences,
} from './api';
import {
  MEMBER_ACCOUNT_COPY,
  MEMBER_MATCHING_PREFERENCES_COPY as COPY,
  MEMBER_PROFILE_ROUTES,
  chipTermsWithOwnRetiredPicks,
  interestChipState,
  matchingPreferencesAreSavable,
} from './constants';
import { MatchingPreferencesForm } from './matching-preferences-form';

/** The five section cards are one shape, said once — `building-from-designs.md`, "reuse before
 *  you write layout", at the scale of a single screen. */
const SECTION_HEADING = 'font-brand text-md font-bold text-foreground';
const SECTION_HINT = 'mb-[14px] mt-[5px] text-sm text-muted-foreground';

interface PreferencesDraft {
  interestTagTermIds: string[];
  currentGoal: string;
  connectionIntentTermIds: string[];
  connectionFormatTermId: string;
  socialComfortTermId: string;
}

export function MatchingPreferencesPage(): React.ReactElement {
  const preferences = useFullMatchingPreferences();
  const vocabularies = useRegistrationVocabularies(true);
  const save = useSaveFullMatchingPreferences();
  const showError = useErrorToast();

  const [draft, setDraft] = React.useState<PreferencesDraft | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (draft !== null || preferences.data === undefined) return;
    const current = preferences.data.current as Record<string, unknown>;
    setDraft({
      interestTagTermIds: [...((current.interestTagTermIds as string[]) ?? [])],
      currentGoal: (current.currentGoal as string) ?? '',
      connectionIntentTermIds: [...((current.connectionIntentTermIds as string[]) ?? [])],
      connectionFormatTermId: (current.connectionFormatTermId as string) ?? '',
      socialComfortTermId: (current.socialComfortTermId as string) ?? '',
    });
  }, [draft, preferences.data]);

  React.useEffect(() => {
    if (preferences.error !== null) {
      showError(preferences.error, 'We could not load your preferences.');
    }
  }, [preferences.error, showError]);

  if (preferences.isLoading || draft === null || preferences.data === undefined) {
    return <Skeleton className="h-96 w-full" />;
  }

  const terms = vocabularies.data?.terms ?? {};
  const termsFor = (vocabulary: string) =>
    (
      terms as Record<
        string,
        ReadonlyArray<{ termId: string; displayName: string; description?: string }>
      >
    )[vocabulary] ?? [];

  const retiredFor = (vocabulary: string) => preferences.data.retiredSelections?.[vocabulary] ?? [];

  const bounds = preferences.data.interestTagSelection;
  const savable = matchingPreferencesAreSavable(draft);

  const update = <K extends keyof PreferencesDraft>(key: K, value: PreferencesDraft[K]) => {
    setSaved(false);
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  };

  const toggleIn = (list: string[], termId: string): string[] =>
    list.includes(termId) ? list.filter((id) => id !== termId) : [...list, termId];

  return (
    <div className="pb-8">
      {/* `mb-9` is the prototype's 36px under the Save row: the top margin of the divider that
          stood here before "Improve your matches" became a card (TW 44260222). The divider went;
          its space was meant to stay (TW 44281877). */}
      <form
        className="mb-9"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(
            {
              interestTagTermIds: draft.interestTagTermIds,
              currentGoal: draft.currentGoal.trim(),
              connectionIntentTermIds: draft.connectionIntentTermIds,
              connectionFormatTermId: draft.connectionFormatTermId,
              socialComfortTermId: draft.socialComfortTermId,
              // No career stage, industry or conversation starter: "Improve your matches" below owns
              // those, and a stale copy sent from here would undo what its own Save just wrote
              // (TW 44260267).
            },
            { onSuccess: () => setSaved(true) },
          );
        }}
      >
        <PageBackLink to={MEMBER_PROFILE_ROUTES.account} className="mb-[18px]">
          {MEMBER_ACCOUNT_COPY.title}
        </PageBackLink>

        <header className="mb-6">
          <h1 className="mb-1.5 text-2xl tracking-tight md:text-4xl">{COPY.title}</h1>
          {/* AC-7's sentence is inside this blurb — "changes apply to your next event" — because a
            released match set is a snapshot and nothing on this screen moves one. */}
          <p className="max-w-[700px] text-md text-muted-foreground md:text-lg">{COPY.blurb}</p>
        </header>

        <Card className="mb-5">
          <CardContent className="p-6">
            <h2 className={SECTION_HEADING}>{COPY.interestsHeading}</h2>
            <p className={SECTION_HINT}>{COPY.interestsHint}</p>
            <ul className="flex flex-wrap gap-[9px]">
              {chipTermsWithOwnRetiredPicks(
                termsFor('interest-tag'),
                retiredFor('interest-tag'),
                draft.interestTagTermIds,
              ).map((term) => {
                const state = interestChipState(draft.interestTagTermIds, term.termId, bounds);
                return (
                  <li key={term.termId}>
                    <ToggleChip
                      density="member"
                      checked={state.active}
                      disabled={state.disabled}
                      onChange={() =>
                        update(
                          'interestTagTermIds',
                          toggleIn(draft.interestTagTermIds, term.termId),
                        )
                      }
                    >
                      {term.displayName}
                    </ToggleChip>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm text-text-subtle">
              {draft.interestTagTermIds.length}/{bounds.max} chosen
            </p>
          </CardContent>
        </Card>

        <Card className="mb-5">
          <CardContent className="p-6">
            {/* The card's heading *is* this control's label, which is what the prototype draws — so
              it is a real `<label>` at the section heading's size rather than a heading with an
              unlabelled textarea under it. */}
            <Field
              label={<span className={SECTION_HEADING}>{COPY.goalHeading}</span>}
              htmlFor="preferences-goal"
              counter={`${draft.currentGoal.length} / 500`}
            >
              <Textarea
                id="preferences-goal"
                rows={4}
                maxLength={500}
                className="min-h-[104px] resize-y rounded-md border-[1.5px] bg-card px-[14px] py-[13px] text-md shadow-none"
                placeholder={COPY.goalHint}
                value={draft.currentGoal}
                onChange={(event) => update('currentGoal', event.target.value)}
              />
            </Field>
          </CardContent>
        </Card>

        <Card className="mb-5">
          <CardContent className="p-6">
            <h2 className={SECTION_HEADING}>{COPY.intentHeading}</h2>
            <p className={SECTION_HINT}>{COPY.intentHint}</p>
            <ul className="flex flex-wrap gap-[9px]">
              {chipTermsWithOwnRetiredPicks(
                termsFor('connection-intent'),
                retiredFor('connection-intent'),
                draft.connectionIntentTermIds,
              ).map((term) => (
                <li key={term.termId}>
                  <ToggleChip
                    density="member"
                    checked={draft.connectionIntentTermIds.includes(term.termId)}
                    onChange={() =>
                      update(
                        'connectionIntentTermIds',
                        toggleIn(draft.connectionIntentTermIds, term.termId),
                      )
                    }
                  >
                    {term.displayName}
                  </ToggleChip>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-5">
          <CardContent className="p-6">
            <h2 className={SECTION_HEADING}>{COPY.comfortHeading}</h2>
            <p className={SECTION_HINT}>{COPY.comfortHint}</p>
            <div className="space-y-2.5">
              {termsFor('social-comfort').map((term) => (
                <SelectableCard
                  key={term.termId}
                  control="radio"
                  name="social-comfort"
                  selected={draft.socialComfortTermId === term.termId}
                  onSelect={() => update('socialComfortTermId', term.termId)}
                  label={term.displayName}
                  description={term.description}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-5">
          <CardContent className="p-6">
            <h2 className={SECTION_HEADING}>{COPY.formatHeading}</h2>
            <p className={SECTION_HINT}>{COPY.formatHint}</p>
            <div className="space-y-2.5">
              {termsFor('connection-format').map((term) => (
                <SelectableCard
                  key={term.termId}
                  control="radio"
                  name="connection-format"
                  selected={draft.connectionFormatTermId === term.termId}
                  // AC-6's "exactly one always selected": selecting sets, and there is no handler
                  // that clears. A member can move the choice; they cannot empty it.
                  onSelect={() => update('connectionFormatTermId', term.termId)}
                  label={term.displayName}
                  description={term.description}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            disabled={!savable || save.isPending}
            className="h-auto rounded-pill px-6 py-[14px] font-brand text-md font-bold"
          >
            {save.isPending ? 'Saving…' : COPY.save}
          </Button>
          {saved ? (
            <span role="status" className="text-sm text-success">
              {COPY.savedNotice}
            </span>
          ) : null}
          {save.error !== null ? (
            <span role="alert" className="text-sm text-destructive">
              That did not save. Try again.
            </span>
          ) : null}
        </div>
      </form>

      {/* `MEM-045`'s four optional fields, on their `preferences` mount.

          **This screen is where that mount moves**, which is what `MEM-045` said would happen:
          it was placed on the old `/app/profile` deliberately, on "the one member surface that
          exists", with the note that "`MEM-009` moves this; it does not rewrite it". Replacing
          that page without bringing the mount would have quietly deleted the only way to edit
          industry and the conversation starter after registration.

          It keeps its own Save, and that is right rather than untidy: it is a different set of
          fields with a different contract — every one optional, each erasable — and merging the
          two saves would mean a member who clears their industry cannot commit it without also
          satisfying the interest floor above. Two explicit saves, neither implicit.

          It is a card like the five sections above it, drawn by `MatchingPreferencesForm` itself so
          no empty card shows while it loads. The prototype draws it on the page background under a
          `margin:36px 0 28px` divider; Sumit chose the card on 2026-09-30 (TW 44260222, "ok with
          option A"), and the divider went with it because the card edge now marks the section.

          **Outside the form above, never inside it** (TW 44260267). It is a form of its own, and
          nested there its submit bubbled to the outer `onSubmit` too, so one press of its Save
          fired both saves at once. */}
      <MatchingPreferencesForm omitAttributes={['socialComfortTermId']} />
    </div>
  );
}
